import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { event as gaEvent } from '../utils/analytics';
import {
    Container,
    Stepper,
    Step,
    StepLabel,
    Button,
    Box,
    Paper,
    Typography
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import BasicInfoForm from '../components/profile/setup/BasicInfoForm';
import PreferencesForm from '../components/profile/setup/PreferencesForm';
import LifestyleForm from '../components/profile/setup/LifestyleForm';

function ProfileSetup() {
    const navigate = useNavigate();
    const { currentUser, updateProfile } = useAuth();
    const { showNotification } = useNotification();
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // プロフィール設定完了済みの場合はプロフィールページにリダイレクト
    useEffect(() => {
        if (currentUser?.isProfileSetupCompleted) {
            showNotification('プロフィール初期設定は既に完了しています', 'info');
            navigate('/profile');
            return;
        }
    }, [currentUser, navigate, showNotification]);

    const [profileData, setProfileData] = useState({
        profile: {
            nickname: '',
            dateOfBirth: null,
            gender: '',
            occupation: '',
            introduction: '',
            profileImage: null,
            sameGenderOnly: false,
            matchingPreferences: {
                smoking: 'non-smoker',
                pets: 'no',
                lifeRhythm: 'morning',
                cleanliness: 'clean',
                communication: 'active',
                stayingHome: 'often',
                visitors: 'welcome'
            },
            preferences: {
                rentRange: {
                    min: 50000,
                    max: 150000
                },
                preferredAreas: [{
                    prefecture: '',
                    city: '',
                    station: '',
                    line: ''
                }]
            },
            stayDuration: 'medium',
            moveInPreference: 'within 3 months'
        }
    });

    const [userRole, setUserRole] = useState(null); // null | 'lister' | 'seeker'

    // 役割に応じたステップ定義
    const steps = userRole === 'lister' ? ['基本情報'] : ['基本情報', '希望条件', 'ライフスタイル'];

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prevStep) => prevStep + 1);
        } else {
            showNotification('必須項目を入力してください', 'error');
        }
    };

    const handleBack = () => {
        if (activeStep === 0) {
            setUserRole(null);
        } else {
            setActiveStep((prevStep) => prevStep - 1);
        }
    };

    const handleSelectRole = (role) => {
        setUserRole(role);
        setActiveStep(0);
        gaEvent({ category: 'conversion', action: 'role_selected', label: role });
    };

    const validateStep = (step) => {
        const profile = profileData.profile;
        switch (step) {
            case 0: // 基本情報
                return !!(
                    profile.nickname &&
                    profile.dateOfBirth &&
                    profile.gender
                );
            case 1: // 希望条件
                return !!(
                    profile.preferences.rentRange.min &&
                    profile.preferences.rentRange.max &&
                    profile.preferences.preferredAreas[0].prefecture
                );
            case 2: // ライフスタイル
                return !!(
                    profile.matchingPreferences.lifeRhythm &&
                    profile.matchingPreferences.cleanliness &&
                    profile.matchingPreferences.communication &&
                    profile.matchingPreferences.stayingHome &&
                    profile.matchingPreferences.visitors
                );
            default:
                return true;
        }
    };

    // 全てのステップのバリデーションを行う
    const validateForm = () => {
        // 全てのステップが正しく入力されているか確認
        for (let i = 0; i < steps.length; i++) {
            if (!validateStep(i)) {
                showNotification(`「${steps[i]}」に必須項目が入力されていません`, 'error');
                setActiveStep(i); // 問題のあるステップに移動
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        // 必須項目のチェック
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);

            // プロフィール情報と完了フラグを同時に更新
            const response = await fetch('/api/users/profile', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    userRole,
                    profile: {
                        ...profileData.profile,
                        preferences: {
                            ...profileData.profile.preferences,
                            lifestyle: undefined // 古いlifestyleフィールドを削除
                        }
                    },
                    completeSetup: true // プロフィール設定完了フラグを同時に設定
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'プロフィールの更新に失敗しました');
            }

            const updatedProfile = await response.json();

            // AuthContextの状態を更新
            await updateProfile({ profile: updatedProfile.profile });

            showNotification('プロフィールを設定しました', 'success');
            gaEvent({ category: 'conversion', action: 'profile_setup_completed', label: userRole });

            // 掲載者は物件登録へ、検索者はプロフィールへ
            const destination = userRole === 'lister' ? '/create-listing' : '/profile';
            setTimeout(() => {
                navigate(destination);
            }, 100);

        } catch (error) {
            console.error('プロフィール設定エラー:', error);
            showNotification(error.message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderRoleSelection = () => (
        <Box>
            <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                どちらの目的でご利用ですか？
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box
                    onClick={() => handleSelectRole('lister')}
                    sx={{
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 3,
                        p: 4,
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'primary.50', transform: 'translateY(-2px)', boxShadow: 4 }
                    }}
                >
                    <Typography variant="h2" sx={{ mb: 1 }}>🏠</Typography>
                    <Typography variant="h6" fontWeight={700} color="primary.main">部屋を掲載したい</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        空き部屋・シェア相手を募集する
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'success.main', fontWeight: 600 }}>
                        ✓ 基本情報のみですぐ掲載可能
                    </Typography>
                </Box>
                <Box
                    onClick={() => handleSelectRole('seeker')}
                    sx={{
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: 'grey.300',
                        borderRadius: 3,
                        p: 4,
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 4 }
                    }}
                >
                    <Typography variant="h2" sx={{ mb: 1 }}>🔍</Typography>
                    <Typography variant="h6" fontWeight={700}>部屋を探したい</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        ルームシェアの部屋・相手を探す
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', fontWeight: 600 }}>
                        希望条件・ライフスタイルも登録
                    </Typography>
                </Box>
            </Box>
        </Box>
    );

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <BasicInfoForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            case 1:
                return (
                    <PreferencesForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            case 2:
                return (
                    <LifestyleForm
                        data={profileData}
                        onChange={setProfileData}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                {userRole === null ? renderRoleSelection() : (
                    <>
                        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {renderStepContent(activeStep)}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <Button onClick={handleBack} sx={{ color: 'text.secondary' }}>
                                ← {activeStep === 0 ? '利用目的を変更' : '戻る'}
                            </Button>
                            {activeStep === steps.length - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? '送信中...' : userRole === 'lister' ? '設定完了して掲載へ' : '設定を完了'}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                >
                                    次へ
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
}

export default ProfileSetup;